import { faAddressCard } from '@fortawesome/free-solid-svg-icons';
import React, { useContext, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import { Client } from '../models/table-models';
import { AddEditClient } from '../pages/AddEditClient';
import { useGetClientByCompanyName as fetchClientByCompanyName } from '../utils/clients-hooks';
import { Role } from '../utils/Enums/Roles';
import { CustomButton } from './CustomButton';

/**
 * Отваря картона на клиента направо от реда на продажбата.
 *
 * По наем същото място води към обекта (BranchActionComponent) - там фактурата виси на обект и имейлът
 * за нея е негов. Продажбата няма обект: тя е към клиента и имейлът за фактурата е на клиента. Затова
 * тук се отваря неговият картон.
 *
 * Причината е същата като при обектите - фактура не тръгва без адрес, а адресът се пише тук. Без този
 * бутон операторът трябва да излезе от продажбите, да намери клиента и да се върне.
 *
 * Клиентът се тегли при натискане, а не предварително за целия списък: страницата показва по няколко
 * десетки реда наведнъж.
 */
export const ClientActionComponent = ({
    companyName,
    refetchEntitiesHook,
    accessEditRoles,
}: {
    companyName?: string;
    refetchEntitiesHook: () => void;
    accessEditRoles?: Role[];
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { hasPermission } = useContext(AuthContext);
    const [client, setClient] = useState<Client>();
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const openClient = async () => {
        if (!companyName) return;
        setLoading(true);
        try {
            // Грешките минават през глобалния интерцептор, който сам показва съобщение.
            const fetched = await fetchClientByCompanyName(companyName);
            if (!fetched?.id) return;
            setClient(fetched);
            setShowModal(true);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Spinner animation='border' size='sm' className='me-1' />;
    }

    return (
        <>
            <CustomButton
                icon={faAddressCard}
                iconClass='fa-fw'
                tooltipTitle={t('client')}
                onClick={openClient}
                className='me-1 color-main'
                disabled={!companyName || (accessEditRoles && !hasPermission([...accessEditRoles, Role.ADMIN]))}
            />
            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop='static' size='lg' centered>
                <Modal.Header closeButton>
                    <Modal.Title id='contained-modal-title-vcenter'>{companyName}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {client && (
                        <AddEditClient itemToEdit={client} {...{ setShowModal, refetchEntitiesHook }} />
                    )}
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button onClick={() => setShowModal(false)}>{t('close')}</Button>
                    <Button form='form' type='submit' className='btn-success'>
                        {t('save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
